/* global G */
import { arrayOf, curry, isStr } from 'lib/util'

/**
 * Recursive data fetcher
 *
 * executes remote api call with given parameters via POST
 *
 * @param fn - callback
 * @param url
 * @param params
 * @param values
 * @returns {Promise<Promise<*|{data: *[], page: *}>|{data: *[], page}>}
 */
export const search = async (fn, url, params, values = []) => {
  const { value, next: page } = await fn({ url, params })
  const data = [...values, ...value]
  return page && data.length < params.limit
    ? await search(fn, url, { ...params, page }, data)
    : { data, page }
}

/**
 * Search Event Handler
 *
 * used for fetching data for rendered iterable ui components
 *
 * requires a predefined set of parameters, where as the filter body should
 * be provided via an action FILTER implementation
 *
 *
 * @param {string} key - listing type, ie short, verbose, etc
 * @param {Gaia.AppModule.Spec} module - app module
 * @param {Gaia.Component.Spec} component - action component
 *
 * @param {Event|object} [eventOrOptions]       information about the event that triggered this
 *                                              handler or a search term
 * @param {Object} [eventOrOptions.detail]      information about the target of the event
 * @returns {Promise<{value: string, key: *}[]>} void
 */
// eslint-disable-next-line no-unused-vars
const searchFn = async (key, module, component, eventOrOptions) => {
  try {
    const componentStateMeta = component[G.STATE][G.META] || {}
    const defaultTransform = { key: key || 'default' }
    const options = (eventOrOptions && eventOrOptions.detail) || eventOrOptions || {}
    const { term: eventTerm, page: eventPage, type: eventType, limit: eventLimit } = options
    const { filter: eventFilter, query: eventQuery } = options
    const { version, api } = module[G.MODEL][G.PROPS]
    const { limit = 10, api: { type = eventType } = {}, sort, unsorted } = component[G.PROPS]
    const { term = eventTerm, filter, query, transform = defaultTransform } = componentStateMeta

    const params = {
      page: eventPage || undefined,
      limit: eventLimit ?? limit,
      sort: !unsorted ? sort || [['name<string>', 'asc']] : [],
      transform,
      query: {
        term,
        type: (query?.type && arrayOf(query.type))
          || (
            query?.includes?.('metatype') || eventQuery?.includes('metatype')
              ? undefined
              : arrayOf(type || api)
          ),
      },
      filter: {
        ...filter,
        ...eventFilter,
      },
    }

    const url = `/api/v${version}/search/global`
    try {
      const remoteAdapter = module[G.MODEL][G.ADAPTER][G.HTTP]

      // const result = await remoteAdapter.post({ url, params })
      // return result.value
      return await search(remoteAdapter.post, url, params)
    } catch {
      console.error('search:: tried and failed getting remote resources', url)
    }
  } catch (e) {
    console.error(e)
  }
  return [
    { key: -1, value: 'Not Found' },
  ]
}

export default curry(searchFn)
